import React from 'react';
import { Icon } from '../components/core/Icon.jsx';
import { Avatar } from '../components/core/Avatar.jsx';
import { ProgressBar } from '../components/data/ProgressBar.jsx';
import type { NavItem } from './nav';

export interface PortalPlan {
  label: string;
  used: number;
  limit: number;
  onUpgrade?: () => void;
}

export interface PortalShellProps {
  nav: NavItem[];
  active: string;
  onNav: (item: NavItem) => void;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  notifCount?: number;
  onBell?: () => void;
  org?: { name: string; verticalLabel?: string };
  user?: { name: string };
  plan?: PortalPlan;
  children: React.ReactNode;
}

/**
 * PortalShell — shell dark del portal negocio (sidebar 240px + topbar).
 * Portado de ui_kits/business_portal/Shell.jsx, parametrizado para React Router.
 */
export function PortalShell({
  nav,
  active,
  onNav,
  title,
  subtitle,
  actions,
  notifCount = 0,
  onBell,
  org,
  user,
  plan,
  children,
}: PortalShellProps) {
  return (
    <div style={{ display: 'flex', minHeight: '100%', height: '100%', background: 'var(--bg)', fontFamily: 'var(--font-sans)' }}>
      <aside style={{ width: 240, flexShrink: 0, background: 'var(--surface)', borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', padding: '22px 14px' }}>
        <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-.02em', color: 'var(--text-strong)', padding: '2px 12px 22px' }}>
          Get<span style={{ color: 'var(--brand-green)' }}>Pay</span>93
        </div>
        <nav style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {nav.map((n) => {
            const on = n.id === active;
            return (
              <button
                key={n.id}
                onClick={() => onNav(n)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 12,
                  border: 'none', cursor: 'pointer', textAlign: 'left', width: '100%',
                  background: on ? 'var(--brand-green-soft)' : 'transparent',
                  color: on ? 'var(--brand-green)' : 'var(--text-faint)',
                  fontWeight: on ? 700 : 600, fontSize: 15, fontFamily: 'var(--font-sans)',
                }}
              >
                <Icon name={n.icon} size={20} />
                <span style={{ flex: 1 }}>{n.label}</span>
                {n.badge ? (
                  <span style={{ minWidth: 20, height: 20, padding: '0 6px', borderRadius: 999, background: on ? 'var(--brand-green)' : 'var(--surface-2)', color: on ? '#fff' : 'var(--text-faint)', fontSize: 11, fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{n.badge}</span>
                ) : null}
              </button>
            );
          })}
        </nav>

        {plan ? (
          <div style={{ marginTop: 'auto', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 14, padding: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-body)' }}>{plan.label}</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-hint)' }}>{plan.used}/{plan.limit}</span>
            </div>
            <ProgressBar value={plan.used} max={plan.limit} height={6} />
            {plan.onUpgrade ? (
              <button onClick={plan.onUpgrade} style={{ marginTop: 12, width: '100%', background: 'transparent', border: '1px solid var(--brand-green)', color: 'var(--brand-green)', borderRadius: 10, padding: '8px', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'var(--font-sans)' }}>Mejorar plan</button>
            ) : null}
          </div>
        ) : (
          <div style={{ marginTop: 'auto' }} />
        )}

        {org ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 8px 2px' }}>
            <Avatar name={org.name} tone="solid" size="sm" />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-body)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{org.name}</div>
              {org.verticalLabel ? <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-hint)', textTransform: 'capitalize' }}>{org.verticalLabel}</div> : null}
            </div>
          </div>
        ) : null}
      </aside>

      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '20px 32px', borderBottom: '1px solid var(--border)' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, letterSpacing: '-.01em', color: 'var(--text-strong)' }}>{title}</h1>
            {subtitle ? <p style={{ margin: '4px 0 0', fontSize: 13, fontWeight: 600, color: 'var(--text-faint)' }}>{subtitle}</p> : null}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            {actions}
            <button onClick={onBell} aria-label="Notificaciones" style={{ position: 'relative', width: 44, height: 44, borderRadius: 12, background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-muted)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="bell" size={20} />
              {notifCount > 0 ? <span style={{ position: 'absolute', top: 6, right: 6, minWidth: 16, height: 16, padding: '0 4px', borderRadius: 999, background: 'var(--status-overdue)', color: '#fff', fontSize: 10, fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{notifCount}</span> : null}
            </button>
            {user ? <Avatar name={user.name} tone="solid" /> : null}
          </div>
        </header>
        <div style={{ flex: 1, overflowY: 'auto', padding: '28px 32px' }}>{children}</div>
      </div>
    </div>
  );
}
